/**
 * React Node View für AuditLink.
 *
 * Lese-Modus: Kompakte einzeilige Inline-Referenz (Icon + Titel + Badge).
 * Edit-Modus: Volle Block-Karte mit Drag-Handle, Meta-Daten, Edit/Delete.
 */

import React, { useState, useEffect, useCallback } from 'react'
import { NodeViewWrapper } from '@tiptap/react'
import { NodeViewProps } from '@tiptap/core'
import { EMBED_DRAG_DATA } from './EmbedDragPlugin'

const AUDIT_COLOR = '#f59e0b'
const POTENTIAL_COLORS: Record<string, string> = {
  'Ja': '#10b981',
  'Nein': '#ef4444',
  'keine abschließende Bewertung möglich': '#f59e0b',
}
const POTENTIAL_SHORT: Record<string, string> = {
  'Ja': 'Ja',
  'Nein': 'Nein',
  'keine abschließende Bewertung möglich': 'Offen',
}

interface AuditData {
  bezeichnung: string
  ebene: string
  funktion: string
  menge: number | null
  einheit: string
  zustand: string
  potentiale: string
  timestamp: string
  cloudId: string
  [key: string]: any
}

export function AuditLinkView({ node, extension, getPos, editor, updateAttributes }: NodeViewProps) {
  const { auditId, label, potentiale } = node.attrs as { auditId: string; label: string; potentiale: string }
  const [isHovered, setIsHovered] = useState(false)
  const [isEditing, setIsEditing] = useState(false)
  const [editLabel, setEditLabel] = useState(label)
  const [auditData, setAuditData] = useState<AuditData | null>(null)
  const isEditable = editor?.isEditable ?? false

  useEffect(() => {
    function resolve() {
      const audits: any[] = (window as any)._auditObjects || []
      const found = audits.find((a: any) => a.id === auditId)
      if (found?.data) setAuditData(found.data)
    }
    resolve()
    const timer = setInterval(resolve, 2000)
    return () => clearInterval(timer)
  }, [auditId])

  function handleClick() {
    if (isEditing) return
    const cb = extension.options.onAuditClick
    if (cb) cb(auditId)
  }

  function handleDragStart(e: React.DragEvent) {
    const pos = getPos()
    if (typeof pos !== 'number') return
    e.dataTransfer.setData(EMBED_DRAG_DATA, JSON.stringify({ pos }))
    e.dataTransfer.effectAllowed = 'move'
    const card = (e.target as HTMLElement).closest('.embed-card')
    if (card) e.dataTransfer.setDragImage(card as HTMLElement, 20, 20)
  }

  const handleDelete = useCallback(() => {
    const pos = getPos()
    if (typeof pos !== 'number') return
    editor.chain().focus().deleteRange({ from: pos, to: pos + node.nodeSize }).run()
  }, [editor, getPos, node.nodeSize])

  const handleEditSave = useCallback(() => {
    updateAttributes({ label: editLabel })
    setIsEditing(false)
  }, [editLabel, updateAttributes])

  const pot = auditData?.potentiale || potentiale
  const potColor = pot ? (POTENTIAL_COLORS[pot] || AUDIT_COLOR) : null
  const potLabel = pot ? (POTENTIAL_SHORT[pot] || pot) : null
  const d = auditData
  const displayLabel = d?.bezeichnung || label || 'Audit'

  function formatDate(iso: string): string {
    try {
      return new Date(iso).toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric' })
    } catch { return iso }
  }

  // ── Lese-Modus: kompakte Inline-Referenz ──────────────────────────────────
  if (!isEditable) {
    return (
      <NodeViewWrapper data-type="audit-link" data-audit-id={auditId}>
        <span
          className="embed-inline"
          onClick={handleClick}
          onMouseEnter={() => setIsHovered(true)}
          onMouseLeave={() => setIsHovered(false)}
          style={{
            color: AUDIT_COLOR,
            background: isHovered ? `${AUDIT_COLOR}12` : 'transparent',
            borderBottomColor: `${AUDIT_COLOR}44`,
          }}
        >
          <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke={AUDIT_COLOR} strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0 }}>
            <path d="M16 4h2a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2h2"/>
            <rect x="8" y="2" width="8" height="4" rx="1" ry="1"/>
          </svg>
          <span className="embed-inline__label">{displayLabel}</span>
          {potColor && potLabel && (
            <span className="embed-inline__badge" style={{ background: `${potColor}20`, color: potColor }}>{potLabel}</span>
          )}
        </span>
      </NodeViewWrapper>
    )
  }

  // ── Edit-Modus: volle Block-Karte ─────────────────────────────────────────
  return (
    <NodeViewWrapper data-type="audit-link" data-audit-id={auditId}>
      <div
        className="embed-card"
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
        style={{
          background: isHovered ? `${AUDIT_COLOR}0d` : `${AUDIT_COLOR}08`,
          borderTopColor: isHovered ? `${AUDIT_COLOR}55` : `${AUDIT_COLOR}33`,
          borderRightColor: isHovered ? `${AUDIT_COLOR}55` : `${AUDIT_COLOR}33`,
          borderBottomColor: isHovered ? `${AUDIT_COLOR}55` : `${AUDIT_COLOR}33`,
          borderLeftColor: AUDIT_COLOR,
          borderLeftWidth: '3px',
        }}
      >
        <div className="embed-card__grip" draggable="true" onDragStart={handleDragStart}>
          <svg width="10" height="16" viewBox="0 0 10 16" fill="currentColor" opacity="0.35">
            <circle cx="3" cy="2" r="1.2"/><circle cx="7" cy="2" r="1.2"/>
            <circle cx="3" cy="6" r="1.2"/><circle cx="7" cy="6" r="1.2"/>
            <circle cx="3" cy="10" r="1.2"/><circle cx="7" cy="10" r="1.2"/>
            <circle cx="3" cy="14" r="1.2"/><circle cx="7" cy="14" r="1.2"/>
          </svg>
        </div>

        <div className="embed-card__body" onClick={handleClick}>
          <div className="embed-card__header">
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke={AUDIT_COLOR} strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0 }}>
              <path d="M16 4h2a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2h2"/>
              <rect x="8" y="2" width="8" height="4" rx="1" ry="1"/>
            </svg>
            {isEditing ? (
              <input
                className="embed-card__edit-input"
                value={editLabel}
                onChange={e => setEditLabel(e.target.value)}
                onBlur={handleEditSave}
                onKeyDown={e => e.key === 'Enter' && handleEditSave()}
                autoFocus
                onClick={e => e.stopPropagation()}
              />
            ) : (
              <span className="embed-card__title" style={{ color: AUDIT_COLOR }}>
                {displayLabel}
              </span>
            )}
            {potColor && potLabel && (
              <span className="embed-card__badge" style={{ background: `${potColor}20`, color: potColor }}>
                {potLabel}
              </span>
            )}
          </div>

          {d && (
            <div className="embed-card__meta">
              {d.funktion && (
                <span className="embed-card__meta-item">
                  <span className="embed-card__meta-label">Funktion</span> {d.funktion}
                </span>
              )}
              {d.ebene && (
                <span className="embed-card__meta-item">
                  <span className="embed-card__meta-label">Ebene</span> {d.ebene}
                </span>
              )}
              {d.zustand && (
                <span className="embed-card__meta-item">
                  <span className="embed-card__meta-label">Zustand</span> {d.zustand}
                </span>
              )}
              {d.menge != null && (
                <span className="embed-card__meta-item">
                  <span className="embed-card__meta-label">Menge</span> {d.menge} {d.einheit}
                </span>
              )}
              {d.timestamp && (
                <span className="embed-card__meta-item embed-card__meta-dim">
                  {formatDate(d.timestamp)}
                </span>
              )}
              <span className="embed-card__meta-item embed-card__meta-dim">
                Wolke: {d.cloudId}
              </span>
            </div>
          )}
        </div>

        {isEditable && isHovered && (
          <div className="embed-card__actions" onClick={e => e.stopPropagation()}>
            <button className="embed-card__action-btn" title="Bearbeiten" onClick={() => { setEditLabel(label); setIsEditing(!isEditing) }}>
              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/>
                <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/>
              </svg>
            </button>
            <button className="embed-card__action-btn embed-card__action-btn--danger" title="Entfernen" onClick={handleDelete}>
              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <polyline points="3 6 5 6 21 6"/><path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"/>
              </svg>
            </button>
          </div>
        )}
      </div>
    </NodeViewWrapper>
  )
}
